/**
 * FL-118: 日次レポート送信履歴ページ
 * 日次レポートの送信履歴・統計・詳細情報を表示
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { dailyReportService } from '../../services/dailyReportService';
import { PageTitle } from '../../components/ui';
import { Button } from '../../components/ui';
import logger from '../../utils/logger';
import type { DailyReportLog } from '../../types';
import {
  EnvelopeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  CalendarIcon,
  UserGroupIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';

const DailyReportLogsPage: React.FC = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState<DailyReportLog[]>([]);
  const [stats, setStats] = useState<{
    totalExecutions: number;
    successfulExecutions: number;
    failedExecutions: number;
    successRate: number;
    averageExecutionTime: number;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<7 | 30 | 90>(30);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const ITEMS_PER_PAGE = 20;

  // 初期データ読み込み
  useEffect(() => {
    if (user?.companyId) {
      loadData();
    }
  }, [user?.companyId, selectedPeriod]);

  const loadData = async () => {
    if (!user?.companyId) return;

    setIsLoading(true);
    try {
      logger.info('🔄 日次レポート履歴取得開始', { companyId: user.companyId });

      // 履歴とStats並行取得
      const [logsData, statsData] = await Promise.all([
        dailyReportService.getExecutionLogs(user.companyId, ITEMS_PER_PAGE, 0),
        dailyReportService.getExecutionStats(user.companyId, selectedPeriod),
      ]);

      setLogs(logsData);
      setStats(statsData);
      setCurrentPage(0);
      setHasMore(logsData.length === ITEMS_PER_PAGE);

      logger.info('✅ 日次レポート履歴取得完了', {
        logsCount: logsData.length,
        stats: statsData,
      });
    } catch (error) {
      logger.error('❌ 日次レポート履歴取得エラー:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // 追加データ読み込み（ページング）
  const loadMore = async () => {
    if (!user?.companyId || isLoading || !hasMore) return;

    setIsLoading(true);
    try {
      const nextPage = currentPage + 1;
      const moreData = await dailyReportService.getExecutionLogs(
        user.companyId,
        ITEMS_PER_PAGE,
        nextPage * ITEMS_PER_PAGE
      );

      setLogs(prev => [...prev, ...moreData]);
      setCurrentPage(nextPage);
      setHasMore(moreData.length === ITEMS_PER_PAGE);
    } catch (error) {
      logger.error('❌ 追加履歴取得エラー:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return (
          <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'>
            <CheckCircleIcon className='w-4 h-4 mr-1' />
            成功
          </span>
        );
      case 'failed':
        return (
          <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'>
            <XCircleIcon className='w-4 h-4 mr-1' />
            失敗
          </span>
        );
      case 'partial':
        return (
          <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'>
            <ClockIcon className='w-4 h-4 mr-1' />
            部分的
          </span>
        );
      default:
        return (
          <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'>
            不明
          </span>
        );
    }
  };

  const getReportTypeLabel = (type: string) => {
    switch (type) {
      case 'case_list':
        return '案件一覧';
      case 'driver_schedule':
        return '陸送員スケジュール';
      case 'combined':
        return '統合レポート';
      default:
        return type;
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  };

  return (
    <div className='container mx-auto py-8 px-4'>
      <PageTitle icon={EnvelopeIcon} title='日次レポート送信履歴' />

      {/* 統計情報 */}
      {stats && (
        <div className='mb-8'>
          <div className='flex items-center justify-between mb-4'>
            <h2 className='text-lg font-medium text-gray-900 dark:text-white'>
              送信統計（過去{selectedPeriod}日間）
            </h2>
            <select
              value={selectedPeriod}
              onChange={e => setSelectedPeriod(Number(e.target.value) as 7 | 30 | 90)}
              className='rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white'
            >
              <option value={7}>過去7日間</option>
              <option value={30}>過去30日間</option>
              <option value={90}>過去90日間</option>
            </select>
          </div>

          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8'>
            <div className='bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6'>
              <div className='flex items-center'>
                <div className='flex-shrink-0'>
                  <ChartBarIcon className='h-8 w-8 text-blue-600 dark:text-blue-400' />
                </div>
                <div className='ml-4'>
                  <p className='text-sm font-medium text-gray-500 dark:text-gray-400'>総実行回数</p>
                  <p className='text-2xl font-semibold text-gray-900 dark:text-white'>
                    {stats.totalExecutions}回
                  </p>
                </div>
              </div>
            </div>

            <div className='bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6'>
              <div className='flex items-center'>
                <div className='flex-shrink-0'>
                  <CheckCircleIcon className='h-8 w-8 text-green-600 dark:text-green-400' />
                </div>
                <div className='ml-4'>
                  <p className='text-sm font-medium text-gray-500 dark:text-gray-400'>成功回数</p>
                  <p className='text-2xl font-semibold text-gray-900 dark:text-white'>
                    {stats.successfulExecutions}回
                  </p>
                </div>
              </div>
            </div>

            <div className='bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6'>
              <div className='flex items-center'>
                <div className='flex-shrink-0'>
                  <div
                    className='h-8 w-8 rounded-full flex items-center justify-center text-white font-bold'
                    style={{
                      backgroundColor:
                        stats.successRate >= 95
                          ? '#10B981'
                          : stats.successRate >= 80
                            ? '#F59E0B'
                            : '#EF4444',
                    }}
                  >
                    %
                  </div>
                </div>
                <div className='ml-4'>
                  <p className='text-sm font-medium text-gray-500 dark:text-gray-400'>成功率</p>
                  <p className='text-2xl font-semibold text-gray-900 dark:text-white'>
                    {stats.successRate}%
                  </p>
                </div>
              </div>
            </div>

            <div className='bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6'>
              <div className='flex items-center'>
                <div className='flex-shrink-0'>
                  <ClockIcon className='h-8 w-8 text-purple-600 dark:text-purple-400' />
                </div>
                <div className='ml-4'>
                  <p className='text-sm font-medium text-gray-500 dark:text-gray-400'>
                    平均実行時間
                  </p>
                  <p className='text-2xl font-semibold text-gray-900 dark:text-white'>
                    {stats.averageExecutionTime}ms
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 送信履歴 */}
      <div className='bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700'>
        <div className='px-6 py-4 border-b border-gray-200 dark:border-gray-700'>
          <h2 className='text-lg font-medium text-gray-900 dark:text-white'>送信履歴</h2>
        </div>

        <div className='divide-y divide-gray-200 dark:divide-gray-700'>
          {isLoading && logs.length === 0 ? (
            <div className='p-8 text-center'>
              <div className='inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600'></div>
              <p className='mt-2 text-gray-500 dark:text-gray-400'>読み込み中...</p>
            </div>
          ) : logs.length === 0 ? (
            <div className='p-8 text-center text-gray-500 dark:text-gray-400'>
              <EnvelopeIcon className='h-12 w-12 mx-auto mb-4 text-gray-300' />
              <p>送信履歴がありません</p>
            </div>
          ) : (
            <>
              {logs.map(log => (
                <div key={log.id} className='p-6'>
                  <div className='flex items-start justify-between'>
                    <div className='flex-1'>
                      <div className='flex items-center space-x-3 mb-2'>
                        {getStatusBadge(log.status)}
                        <span className='text-sm font-medium text-gray-900 dark:text-white'>
                          {getReportTypeLabel(log.reportType)}
                        </span>
                        <span className='text-sm text-gray-500 dark:text-gray-400'>
                          {new Date(log.sentAt).toLocaleString('ja-JP')}
                        </span>
                      </div>

                      <div className='grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600 dark:text-gray-400'>
                        <div className='flex items-center space-x-2'>
                          <CalendarIcon className='h-4 w-4' />
                          <span>
                            対象期間: {log.dateRangeStart}
                            {log.dateRangeStart !== log.dateRangeEnd && ` 〜 ${log.dateRangeEnd}`}
                          </span>
                        </div>
                        <div className='flex items-center space-x-2'>
                          <UserGroupIcon className='h-4 w-4' />
                          <span>送信先: {log.recipients.length}件</span>
                        </div>
                        <div className='flex items-center space-x-2'>
                          <ClockIcon className='h-4 w-4' />
                          <span>実行時間: {log.executionTimeMs}ms</span>
                        </div>
                      </div>

                      <div className='mt-3 flex flex-wrap gap-4 text-xs text-gray-500'>
                        <span>案件数: {log.casesCount || 0}</span>
                        <span>陸送員数: {log.driversCount || 0}</span>
                        {log.csvFileSizeBytes && (
                          <span>CSV: {formatFileSize(log.csvFileSizeBytes)}</span>
                        )}
                        {log.pdfFileSizeBytes && (
                          <span>PDF: {formatFileSize(log.pdfFileSizeBytes)}</span>
                        )}
                      </div>

                      {log.errorMessage && (
                        <div className='mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md'>
                          <div className='flex items-start space-x-2'>
                            <XCircleIcon className='h-4 w-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0' />
                            <div>
                              <p className='text-sm font-medium text-red-800 dark:text-red-200'>
                                エラー詳細
                              </p>
                              <p className='text-sm text-red-700 dark:text-red-300 mt-1'>
                                {log.errorMessage}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {log.recipients.length > 0 && (
                        <details className='mt-3'>
                          <summary className='cursor-pointer text-sm text-blue-600 dark:text-blue-400 hover:underline'>
                            送信先詳細を表示
                          </summary>
                          <div className='mt-2 p-3 bg-gray-50 dark:bg-gray-700 rounded-md'>
                            <div className='flex flex-wrap gap-2'>
                              {log.recipients.map((email, index) => (
                                <span
                                  key={index}
                                  className='inline-block px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs rounded'
                                >
                                  {email}
                                </span>
                              ))}
                            </div>
                          </div>
                        </details>
                      )}
                    </div>

                    <div className='ml-4'>
                      {log.fileFormats?.map((format, index) => (
                        <span
                          key={index}
                          className='inline-block px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 text-xs rounded mr-1'
                        >
                          {format.toUpperCase()}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}

              {hasMore && (
                <div className='p-6 text-center border-t border-gray-200 dark:border-gray-700'>
                  <Button variant='secondary' onClick={loadMore} disabled={isLoading}>
                    {isLoading ? '読み込み中...' : 'さらに読み込む'}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DailyReportLogsPage;
